import React, { useEffect, useState } from "react";
import { createPortal } from "react-dom";
import { getStorageDownloadURL } from "@/components/Firebase/firebase";
import Icon, { IconName } from "./icons";
import { T, TONE, cardShadow } from "./tokens";
import { decodeAnnualIncome, displayValue, maskAccountNumber } from "./maps";
import { KycImage, SectionData } from "./types";

type SectionId =
  | "identity"
  | "contact"
  | "address"
  | "employment"
  | "risk"
  | "settlement"
  | "docs";

const SECTION_ORDER: SectionId[] = [
  "identity",
  "contact",
  "address",
  "employment",
  "risk",
  "settlement",
  "docs",
];

const META: Record<SectionId, { title: string; icon: IconName }> = {
  identity: { title: "Verified identity", icon: "shieldCheck" },
  contact: { title: "Contact details", icon: "mail" },
  address: { title: "Address", icon: "mapPin" },
  employment: { title: "Employment & income", icon: "briefcase" },
  risk: { title: "Risk profile", icon: "trendingUp" },
  settlement: { title: "Settlement / bank account", icon: "bank" },
  docs: { title: "Identity documents", icon: "file" },
};

const DOC_ALIASES: Record<string, string[]> = {
  idPhotoFront: ["idPhotoFront", "secondary1"],
  idPhotoBack: ["idPhotoBack", "secondary2"],
};

const capitalize = (s: string): string => (s ? s.charAt(0).toUpperCase() + s.slice(1) : s);

function viewRows(id: SectionId, v: SectionData): { label: string; value: string; full?: boolean }[] {
  switch (id) {
    case "identity":
      return [
        { label: "Full Name", value: displayValue(v.identity.fullName) },
        { label: "Date of Birth", value: displayValue(v.identity.dob) },
        { label: "Nationality", value: displayValue(v.identity.nationality) },
        { label: "TIN", value: displayValue(v.identity.tin) },
      ];
    case "contact":
      return [
        { label: "Email", value: displayValue(v.contact.email), full: true },
        { label: "Mobile Number", value: displayValue(v.contact.mobileNumber) },
      ];
    case "address":
      return [
        { label: "Street Address", value: displayValue(v.address.street), full: true },
        { label: "City", value: displayValue(v.address.city) },
        { label: "Province", value: displayValue(v.address.province) },
        { label: "ZIP Code", value: displayValue(v.address.zip) },
        { label: "Country", value: displayValue(v.address.country) },
      ];
    case "employment":
      return [
        { label: "Occupation", value: displayValue(v.employment.primaryOccupation) },
        { label: "Employer", value: displayValue(v.employment.nameOfEmployer) },
        { label: "Nature of Work", value: displayValue(v.employment.businessNature), full: true },
        { label: "Annual Gross Income", value: decodeAnnualIncome(v.employment.annualIncomeAmount) },
        { label: "Source of Funds", value: displayValue(v.employment.sourceOfFunds) },
      ];
    case "risk":
      return [
        { label: "Risk Appetite", value: v.risk.appetite ? capitalize(v.risk.appetite) : "\u2014", full: true },
      ];
    case "settlement":
      return [
        { label: "Bank", value: displayValue(v.settlement.bankName) },
        { label: "Account Name", value: displayValue(v.settlement.accountName) },
        { label: "Account Number", value: maskAccountNumber(v.settlement.bankAccountNumber) },
        {
          label: "Account Type",
          value:
            v.settlement.savingsAccount === ""
              ? "\u2014"
              : v.settlement.savingsAccount
              ? "Savings Account"
              : "Current Account",
        },
      ];
    default:
      return [];
  }
}

export interface AccountSectionsProps {
  data: SectionData;
  /** Only verified accounts get the "Verified" badge + locked-identity note. */
  verified: boolean;
}

const AccountSections: React.FC<AccountSectionsProps> = ({ data, verified }) => (
  <div style={{ display: "flex", flexDirection: "column", gap: 18 }}>
    {/* Header */}
    <div style={{ display: "flex", alignItems: "flex-start", gap: 14 }}>
      <span style={iconTile46}>
        <Icon name="user" size={22} color={T.green} />
      </span>
      <div style={{ flex: 1, minWidth: 0 }}>
        <h3 style={{ margin: 0, fontWeight: 700, fontSize: 21, lineHeight: 1.1, color: T.heading, letterSpacing: "-.01em" }}>
          Account Overview
        </h3>
        <p style={{ margin: "4px 0 0", fontSize: 13, lineHeight: 1.45, color: T.muted, maxWidth: 560 }}>
          A complete snapshot of the information on file for your account.
        </p>
      </div>
    </div>

    {/* Sections */}
    <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
      {SECTION_ORDER.map((id) => (
          <div
            key={id}
            style={{
              background: "#fff",
              border: `1.5px solid ${T.hairline}`,
              borderRadius: 14,
              overflow: "hidden",
              boxShadow: cardShadow,
            }}
          >
            <div
              style={{
                display: "flex",
                alignItems: "center",
                gap: 13,
                padding: "16px 22px",
                borderBottom: `1px solid ${T.hairline3}`,
              }}
            >
              <span style={iconTile38}>
                <Icon name={META[id].icon} size={19} color={id === "identity" ? "#1A7A4E" : T.green} />
              </span>
              <span style={{ flex: 1, minWidth: 0, fontWeight: 700, fontSize: 15, color: T.body }}>
                {id === "identity"
                  ? verified
                    ? "Verified identity"
                    : "Identity"
                  : META[id].title}
              </span>
              {id === "identity" && verified && (
                <span
                  style={{
                    display: "inline-flex",
                    alignItems: "center",
                    gap: 6,
                    fontWeight: 600,
                    fontSize: 11,
                    padding: "5px 11px",
                    borderRadius: 20,
                    whiteSpace: "nowrap",
                    color: TONE.verified.fg,
                    background: TONE.verified.bg,
                    border: `1px solid ${TONE.verified.bd}`,
                  }}
                >
                  <Icon name="check" size={13} color={TONE.verified.fg} /> Verified
                </span>
              )}
            </div>

            <div style={{ padding: "18px 22px 20px" }}>
              {id === "docs" ? (
                <DocsView images={data.docs} />
              ) : (
                <ViewGrid rows={viewRows(id, data)} />
              )}
              {id === "identity" && verified && (
                <div
                  style={{
                    display: "flex",
                    alignItems: "center",
                    gap: 9,
                    marginTop: 16,
                    paddingTop: 14,
                    borderTop: `1px solid ${T.hairline3}`,
                    fontSize: 12,
                    lineHeight: 1.4,
                    color: T.muted2,
                  }}
                >
                  <Icon name="lock" size={15} color="#A0A0A0" />
                  These details are verified and locked. To change them, please contact support to
                  re-verify your identity.
                </div>
              )}
            </div>
          </div>
      ))}
    </div>
  </div>
);

const ViewGrid: React.FC<{ rows: { label: string; value: string; full?: boolean }[] }> = ({ rows }) => (
  <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: "14px 28px" }}>
    {rows.map((r) => (
      <div key={r.label} style={r.full ? { gridColumn: "1 / -1" } : undefined}>
        <div
          style={{
            fontWeight: 500,
            fontSize: 11,
            color: T.muted3,
            textTransform: "uppercase",
            letterSpacing: ".04em",
            marginBottom: 3,
          }}
        >
          {r.label}
        </div>
        <div style={{ fontWeight: 600, fontSize: 14, color: T.body }}>{r.value}</div>
      </div>
    ))}
  </div>
);

const swatchBg =
  "repeating-linear-gradient(135deg,#EEF1EF,#EEF1EF 6px,#F7F9F7 6px,#F7F9F7 12px)";

const PREVIEWABLE_EXTENSIONS = ["png", "jpg", "jpeg", "webp", "gif", "bmp"];

// Desktop-local image-previewability check (kept self-contained, no cross-feature
// import). HEIC/HEIF and PDF aren't reliably <img>-previewable, so those open in
// a new tab instead of rendering a blank thumbnail.
function isPreviewableImage(meta?: KycImage): boolean {
  if (!meta) return false;
  const mime = (meta.mimeType || "").toLowerCase();
  if (mime) return mime.startsWith("image/") && mime !== "image/heic" && mime !== "image/heif";
  const source = (meta.fileName || meta.url || "").toLowerCase().split("?")[0];
  const ext = source.includes(".") ? source.split(".").pop() || "" : "";
  return PREVIEWABLE_EXTENSIONS.includes(ext);
}

// Resolves an on-file ID document (Storage path) to a thumbnail. Previewable
// images open a full-size lightbox on click; non-previewable docs (PDF/HEIC)
// open in a new tab. Shows the striped placeholder while loading or when the
// object is missing.
const DocTile: React.FC<{
  meta?: KycImage;
  label: string;
  onView: (url: string, title: string) => void;
}> = ({ meta, label, onView }) => {
  const [url, setUrl] = useState<string | null>(null);
  const [failed, setFailed] = useState(false);

  useEffect(() => {
    const path = meta?.url;
    if (!path) return undefined;
    if (/^https?:\/\//i.test(path)) {
      setUrl(path);
      return undefined;
    }
    let active = true;
    (async () => {
      try {
        const resolved: string = await getStorageDownloadURL(path);
        if (active) setUrl(resolved);
      } catch {
        if (active) setFailed(true);
      }
    })();
    return () => {
      active = false;
    };
  }, [meta?.url]);

  const fileName = meta?.fileName || meta?.name || "On file";
  const previewable = isPreviewableImage(meta) && !failed;
  const hasUrl = Boolean(url);
  const showImage = hasUrl && previewable;

  // Previewable images open the lightbox; resolved-but-not-previewable docs
  // (PDF/HEIC) open in a new tab instead of showing a blank placeholder.
  const handleOpen = () => {
    if (!url) return;
    if (previewable) onView(url, label);
    else window.open(url, "_blank", "noopener,noreferrer");
  };

  return (
    <div
      onClick={handleOpen}
      style={{
        border: `1px solid ${T.hairline}`,
        borderRadius: 12,
        padding: 13,
        display: "flex",
        alignItems: "center",
        gap: 13,
        cursor: hasUrl ? "pointer" : "default",
      }}
    >
      <div
        style={{
          width: 64,
          height: 44,
          borderRadius: 7,
          overflow: "hidden",
          background: swatchBg,
          border: "1px solid #E0DCDC",
          flexShrink: 0,
        }}
      >
        {showImage && url ? (
          <img
            src={url}
            alt=""
            onError={() => setFailed(true)}
            style={{ width: "100%", height: "100%", objectFit: "cover", display: "block" }}
          />
        ) : (
          hasUrl && (
            <span style={{ width: "100%", height: "100%", display: "grid", placeItems: "center" }}>
              <Icon name="file" size={20} color={T.green} />
            </span>
          )
        )}
      </div>
      <div style={{ flex: 1, minWidth: 0 }}>
        <div style={{ fontWeight: 600, fontSize: 13, color: T.body }}>{label}</div>
        <div style={{ fontSize: 11.5, color: T.muted3, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap" }}>
          {fileName}
        </div>
      </div>
      {hasUrl && (
        <span style={{ display: "inline-flex", alignItems: "center", gap: 5, color: T.green, fontWeight: 600, fontSize: 12.5, flexShrink: 0 }}>
          <Icon name={previewable ? "eye" : "file"} size={14} /> {previewable ? "View" : "Open"}
        </span>
      )}
    </div>
  );
};

const DocLightbox: React.FC<{ src: string; title: string; onClose: () => void }> = ({
  src,
  title,
  onClose,
}) => {
  useEffect(() => {
    const onKey = (event: KeyboardEvent) => {
      if (event.key === "Escape") onClose();
    };
    document.addEventListener("keydown", onKey);
    return () => document.removeEventListener("keydown", onKey);
  }, [onClose]);

  // Portal to body: an ancestor (.layout) animates a transform, which would
  // otherwise make this fixed overlay resolve against that ancestor box.
  return createPortal(
    <div
      role="dialog"
      aria-modal="true"
      aria-label={title}
      onClick={onClose}
      style={{
        position: "fixed",
        top: 0,
        left: 0,
        right: 0,
        bottom: 0,
        zIndex: 1400,
        background: "rgba(15,23,20,.82)",
        display: "flex",
        flexDirection: "column",
        alignItems: "center",
        justifyContent: "center",
        padding: 24,
      }}
    >
      <div
        onClick={(event) => event.stopPropagation()}
        style={{ width: "min(900px, 92vw)", display: "flex", alignItems: "center", justifyContent: "space-between", marginBottom: 12 }}
      >
        <span style={{ color: "#fff", fontSize: 14, fontWeight: 600, overflow: "hidden", textOverflow: "ellipsis", whiteSpace: "nowrap", marginRight: 12 }}>
          {title}
        </span>
        <button
          type="button"
          onClick={onClose}
          aria-label="Close preview"
          style={{ border: "none", background: "rgba(255,255,255,.14)", color: "#fff", borderRadius: 8, width: 36, height: 36, fontSize: 18, lineHeight: 1, cursor: "pointer", flexShrink: 0 }}
        >
          {"\u2715"}
        </button>
      </div>
      <div
        onClick={(event) => event.stopPropagation()}
        style={{ maxWidth: "min(900px, 92vw)", maxHeight: "80vh", overflow: "auto", borderRadius: 12, background: "#fff" }}
      >
        <img src={src} alt={title} style={{ display: "block", maxWidth: "100%", height: "auto" }} />
      </div>
    </div>,
    document.body
  );
};

const DocsView: React.FC<{ images: KycImage[] }> = ({ images }) => {
  const [preview, setPreview] = useState<{ url: string; title: string } | null>(null);
  const metaFor = (type: string) => {
    const keys = DOC_ALIASES[type] || [type];
    return images.find((m) => keys.includes(m.image));
  };
  const DOCS = [
    { type: "idPhotoFront", label: "Government ID (Front)" },
    { type: "idPhotoBack", label: "Government ID (Back)" },
  ];
  return (
    <>
      <div style={{ display: "grid", gridTemplateColumns: "1fr 1fr", gap: 16 }}>
        {DOCS.map((d) => (
          <DocTile
            key={d.type}
            meta={metaFor(d.type)}
            label={d.label}
            onView={(url, title) => setPreview({ url, title })}
          />
        ))}
      </div>
      {preview && (
        <DocLightbox src={preview.url} title={preview.title} onClose={() => setPreview(null)} />
      )}
    </>
  );
};

const iconTile46: React.CSSProperties = {
  width: 46,
  height: 46,
  borderRadius: 12,
  background: T.mint2,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};
const iconTile38: React.CSSProperties = {
  width: 38,
  height: 38,
  borderRadius: 10,
  background: T.mint2,
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};

export default AccountSections;
